import {
  searchTags,
  getTagsByIds,
  fetchRecommendedTags,
} from "@/shared/api/newsquawk-headlines";
import { pickRecommended } from "./pickRecommended";
import { labelMatch, type MatchStrength } from "./tagSearch";
import {
  isSafeToScan,
  resolveTerm,
  symbolLabel,
  tagKind,
  type LabelCategory,
  type ResolvedTag,
  type ResolvedTerm,
  type TagCategory,
} from "./resolveTerm";

/**
 * Resolving typed or pasted terms against the tag service.
 *
 * The whole taxonomy is deliberately NOT fetched. `/api/tags/simple` would
 * return every tag and make this trivial, but it puts a curated corpus of tens
 * of thousands of tags in the browser — a megabyte of memory on a terminal
 * already short of it, and the taxonomy itself one devtools tab from being
 * copied. Lookups only ever ask about terms the user already typed.
 *
 * Transport is isolated here: when a batch `POST /tags/resolve` exists, only
 * the lookup inside `resolveTerms` changes — callers keep the same interface.
 */

/** Concurrent lookups. Enough to import a CSV briskly, gentle on rate limits. */
const CONCURRENCY = 6;

/**
 * How many alternates to offer the user when we decline to pick one.
 *
 * This bounds what we *show*, never what we *consider*. `/tags/search` is a
 * substring match returned in tag-id order, with no relevance score — so the
 * best answer is routinely not first. Searching "OPEC" returns twelve hits with
 * the actual OPEC tag tenth, behind seven Sinopec subsidiaries. Truncating
 * before scoring threw that answer away.
 */
const MAX_ALTERNATES = 5;

export interface SearchHit {
  id: number;
  labels?: {
    name: string;
    primary?: boolean;
    category?: string;
    classification?: string;
    scanning_importance?: number;
  }[];
  /** Present on some deployments; absent ones are backfilled via /tags/ids. */
  category?: string;
  relations_to?: { to_id: number; confidence?: number }[];
  [key: string]: unknown;
}

/** Runs tasks with a bounded number in flight, preserving input order. */
async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  task: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await task(items[index], index);
    }
  });
  await Promise.all(workers);
  return results;
}

/**
 * Backfills `category` when search omits it.
 *
 * Instrument-vs-topic depends entirely on category, and without it every term
 * would degrade to a topic. `/tags/ids/{ids}` returns TagDetails, which always
 * carries it, and takes many ids at once — so this costs one extra request for
 * a whole batch rather than one per term.
 */
async function backfillCategories(
  hits: SearchHit[],
  signal?: AbortSignal
): Promise<Map<number, TagCategory>> {
  const missing = [...new Set(hits.filter((h) => !h.category).map((h) => h.id))];
  if (missing.length === 0) return new Map();
  try {
    const details = await getTagsByIds(missing, signal);
    const found = new Map<number, TagCategory>();
    for (const [id, detail] of Object.entries(details ?? {})) {
      if (detail?.category) found.set(Number(id), detail.category as TagCategory);
    }
    return found;
  } catch {
    // Non-fatal: terms fall back to keywords, which still match text.
    return new Map();
  }
}

function toResolvedTag(hit: SearchHit, category?: TagCategory): ResolvedTag {
  return {
    id: hit.id,
    category: (hit.category as TagCategory) ?? category,
    labels: (hit.labels ?? []).map((label) => ({
      name: label.name,
      primary: label.primary,
      category: label.category as LabelCategory | undefined,
      // Carries the only signal that distinguishes "AAPL" from "Apple Inc".
      classification: label.classification,
      scanning_importance: label.scanning_importance,
    })),
  };
}

// The match rule lives with the tag-library ranking now, so the concierge's
// term resolution and the search boxes score a label the same way.
export type { MatchStrength };

export interface Candidate {
  hit: SearchHit;
  strength: MatchStrength;
  label: string;
}

/**
 * The best candidate for a term, or nothing.
 *
 * Returning nothing is a real answer: the term stays a plain keyword, which
 * still matches text on every feed. That is the right outcome for "Cook",
 * where the only leading match is "Cook Islands" — a label the desk already
 * marked unsafe to scan — and the alternative is silently attaching a
 * geography tag to a question about the Fed.
 *
 * An exact match ignores the scanning floor: the floor guards *inference*, and
 * a user who types a label in full has not inferred anything.
 */
function bestCandidate(term: string, hits: SearchHit[]): Candidate | undefined {
  const candidates: Candidate[] = [];
  for (const hit of hits) {
    for (const label of hit.labels ?? []) {
      if (!label?.name) continue;
      const strength = labelMatch(term, label.name);
      if (strength === "weak") continue;
      if (strength === "leading" && !isSafeToScan(label)) continue;
      candidates.push({ hit, strength, label: label.name });
    }
  }
  if (candidates.length === 0) return undefined;

  const rank = (candidate: Candidate) => (candidate.strength === "exact" ? 0 : 1);
  // Shortest label wins a tie: "Apple Inc" over "Apple Flavor & Fragrance
  // Group Co Ltd" — the least additional wording is the closest reading.
  return [...candidates].sort(
    (a, b) => rank(a) - rank(b) || a.label.length - b.label.length
  )[0];
}

/**
 * Finds the ticker tag belonging to a company tag.
 *
 * Searching a company name never reaches its ticker: `/tags/search` matches
 * label substrings, and the AAPL tag's labels are AAPL / AAPL.OQ / its ISIN,
 * none of which contain "apple". The relation that connects them runs the
 * wrong way — ticker → company — and the proxy exposes no reverse lookup.
 *
 * What it does expose is that the taxonomy stores the pair adjacently: the
 * ticker tag is the company's id + 1. Verified on 93 companies (US, UK, EU,
 * Asian, ETFs, investment trusts): 96% had it, and every single one at +1.
 *
 * Adjacency alone would be a guess, so it is never trusted on its own — the
 * neighbour is accepted only if it really is a `ticker` AND its own
 * `relations_to` points back at the company we started from. A taxonomy
 * reshuffle therefore costs us the ticker, never a wrong one: the three misses
 * in that sample were all rejected here rather than mis-attached.
 *
 * One batched request covers every company in the batch.
 */
async function linkedTickersFor(
  companyIds: number[],
  signal?: AbortSignal
): Promise<Map<number, string>> {
  const found = new Map<number, string>();
  if (companyIds.length === 0) return found;
  try {
    const details = await getTagsByIds(
      companyIds.map((id) => id + 1),
      signal
    );
    for (const companyId of companyIds) {
      const neighbour = details?.[String(companyId + 1)];
      if (!neighbour || neighbour.category !== "ticker") continue;
      const pointsBack = (neighbour.relations_to ?? []).some(
        (relation) => relation.to_id === companyId
      );
      if (!pointsBack) continue;
      const symbol = symbolLabel({
        id: companyId + 1,
        category: "ticker",
        labels: neighbour.labels,
      });
      if (symbol) found.set(companyId, symbol);
    }
  } catch {
    // Non-fatal: the term keeps the company tag and stays a keyword.
  }
  return found;
}

/**
 * Trades an exchange-suffixed symbol for the plain one, where the taxonomy
 * holds both.
 *
 * Two ticker families exist. The older one carries a single suffixed label
 * (`MSFT.US`, `TCG.LN`, `2193.JT`); the newer one carries the plain symbol
 * plus its RIC and ISIN (`MSFT`, `MSFT.OQ`, …). Which one a company reaches
 * depends only on which family it was ingested into — Microsoft is in the old
 * one, Apple the new — and the feeds match plain tickers as free text, where
 * `MSFT` matches "MSFT.US" but `MSFT.US` matches nothing.
 *
 * The stem is used only as a *search query*, never as the stored value: the
 * result counts only if some ticker tag carries it as an exact label AND
 * relates back to the very company we started from. So this cannot invent
 * `BRK` from `BRK.B` — no ticker tag claims "BRK" for Berkshire's class B —
 * and a wrong guess simply finds nothing and keeps the suffixed form.
 */
async function preferPlainSymbol(
  companyId: number,
  symbol: string,
  signal?: AbortSignal
): Promise<string> {
  const cut = symbol.lastIndexOf(".");
  if (cut <= 0) return symbol;
  const stem = symbol.slice(0, cut);
  try {
    const hits = ((await searchTags(stem, signal)) ?? []) as SearchHit[];
    const plain = hits.find(
      (hit) =>
        hit.category === "ticker" &&
        (hit.labels ?? []).some((label) => label.name === stem) &&
        (hit.relations_to ?? []).some((relation) => relation.to_id === companyId)
    );
    return plain ? stem : symbol;
  } catch {
    return symbol;
  }
}

export interface ResolveTermsResult {
  resolved: ResolvedTerm[];
  /** Terms with more than one plausible tag — the caller should confirm. */
  ambiguous: Record<string, { id: number; label: string }[]>;
}

/**
 * Resolves each term to a tag where one exists.
 *
 * An unresolved term is never dropped: it comes back as a keyword, which still
 * matches text on every feed, so a lookup failure degrades to today's
 * behaviour rather than losing what the user typed.
 */

async function recommendCandidate(
  term: string,
  signal?: AbortSignal
): Promise<Candidate | undefined> {
  try {
    return pickRecommended(term, await fetchRecommendedTags(term, signal));
  } catch {
    return undefined;
  }
}

export async function resolveTerms(
  terms: string[],
  options: { signal?: AbortSignal; onProgress?: (done: number, total: number) => void } = {}
): Promise<ResolveTermsResult> {
  const unique = [...new Set(terms.map((t) => t.trim()).filter(Boolean))];
  if (unique.length === 0) return { resolved: [], ambiguous: {} };

  let done = 0;
  const hitsPerTerm = await mapWithConcurrency(unique, CONCURRENCY, async (term) => {
    try {
      return ((await searchTags(term, options.signal)) ?? []) as SearchHit[];
    } catch {
      return [] as SearchHit[];
    } finally {
      options.onProgress?.(++done, unique.length);
    }
  });

  const categories = await backfillCategories(hitsPerTerm.flat(), options.signal);

  // Pick winners first, then recover symbols for the company tags among them
  // in a single request — "Apple" and "Tesla" in one paste cost one lookup.
  const searchWinners = unique.map((term, index) =>
    bestCandidate(term, hitsPerTerm[index])
  );

  /**
   * Anything the substring search couldn't place gets a second opinion from
   * the recommender. Only the gaps, so a paste of well-known names costs
   * nothing extra, and the same concurrency cap applies.
   */
  const needsRecommend = unique
    .map((term, index) => (searchWinners[index] ? null : index))
    .filter((index): index is number => index !== null);

  const recommended = await mapWithConcurrency(
    needsRecommend,
    CONCURRENCY,
    (index) => recommendCandidate(unique[index], options.signal)
  );

  const winners = [...searchWinners];
  needsRecommend.forEach((index, slot) => {
    winners[index] = recommended[slot];
  });
  const companyIds = winners
    .filter((winner): winner is Candidate => !!winner)
    .map((winner) => toResolvedTag(winner.hit, categories.get(winner.hit.id)))
    .filter((tag) => tag.category === "company")
    .map((tag) => tag.id);
  const linkedTickers = await linkedTickersFor([...new Set(companyIds)], options.signal);
  // Only the older, suffix-only family needs this, so it usually costs nothing.
  await Promise.all(
    [...linkedTickers].map(async ([companyId, symbol]) => {
      linkedTickers.set(
        companyId,
        await preferPlainSymbol(companyId, symbol, options.signal)
      );
    })
  );

  const ambiguous: ResolveTermsResult["ambiguous"] = {};
  const resolved = unique.map((term, index) => {
    const hits = hitsPerTerm[index];
    const best = winners[index];

    // Offer alternates whenever we picked something short of an exact match,
    // or declined to pick at all but the search did return possibilities.
    if (hits.length > 0 && best?.strength !== "exact") {
      const alternates = hits
        .filter((hit) => hit.id !== best?.hit.id)
        .slice(0, MAX_ALTERNATES)
        .map((hit) => ({
          id: hit.id,
          label:
            (hit.labels ?? []).find((label) => label.primary)?.name ??
            hit.labels?.[0]?.name ??
            String(hit.id),
        }));
      if (alternates.length > 0) ambiguous[term] = alternates;
    }

    const tag = best ? toResolvedTag(best.hit, categories.get(best.hit.id)) : undefined;
    return resolveTerm(term, tag, tag ? linkedTickers.get(tag.id) : undefined);
  });

  return { resolved, ambiguous };
}
